import { useLocation } from 'react-router-dom';

import { useOpenPortal } from '@/features/billing/use-open-portal';
import { useUndoCancelSubscription } from '@/features/billing/use-cancel-subscription';
import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { urlOverlays } from '@/constants/url-overlays';

import type { EnergyAction } from './energy-view';

export const energyActionLabels: Record<EnergyAction, string> = {
  exploreTrial: 'Explore Trial',
  comparePlans: 'Compare plans',
  startPlan: 'Start my plan now',
  payHeldCharge: 'Complete payment',
  undoCancel: 'Undo cancellation',
  updatePayment: 'Update payment method',
  recharge: 'Recharge',
};

/**
 * Runs an Energy action: the plan picker, the Recharge overlay, the Start plan overlay (also for a held charge), the
 * cancellation's undo, or the Stripe portal. `opening` is true while the portal is being created or the undo is pending.
 */
export const useEnergyAction = () => {
  const { pathname, search } = useLocation();
  const { open: openPlanPicker } = useUrlOverlay(urlOverlays.planPicker, urlOverlays);
  const { open: openRecharge } = useUrlOverlay(urlOverlays.recharge, urlOverlays);
  const { open: openStartPlan } = useUrlOverlay(urlOverlays.startPlan, urlOverlays);
  const { openPortal, opening: openingPortal } = useOpenPortal(pathname + search);
  const { undoCancelSubscription, isPending: undoingCancel } = useUndoCancelSubscription();

  const runEnergyAction = (action: EnergyAction) => {
    switch (action) {
      case 'exploreTrial':
      case 'comparePlans':
        openPlanPicker();
        break;
      case 'startPlan':
      case 'payHeldCharge':
        openStartPlan();
        break;
      case 'undoCancel':
        void undoCancelSubscription();
        break;
      case 'updatePayment':
        void openPortal();
        break;
      case 'recharge':
        openRecharge();
        break;
    }
  };

  return { runEnergyAction, opening: openingPortal || undoingCancel };
};
